import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Component, Input, OnInit } from '@angular/core';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { ConfigService } from '../../shared-services/config.service';

type NoteItem = { id: number; label: string; text: string };

interface NotesData {
  notes?: NoteItem[];
  notesApiUrl?: string;
  latestDataApiUrl?: string;
  apiUrl?: string;
  marginTop?: string;
}

@Component({
  selector: 'app-notes',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './notes.html',
  styleUrl: './notes.css',
})
export class Notes implements OnInit {
  private _data?: NotesData;
  @Input() set data(value: NotesData | undefined) {
    this._data = value;
    if (value) this.fetchNotes();
  }
  get data(): NotesData | undefined {
    return this._data;
  }

  notes: NoteItem[] = [];

  constructor(private http: HttpClient, private configService: ConfigService) { }

  ngOnInit(): void { }

  private fetchNotes(): void {
    console.log('[Notes Component] fetchNotes called with data:', this.data);
    const notesUrl = this.data?.notesApiUrl || '/assets/notes/notes-config.json';
    const latestDataUrl = this.data?.latestDataApiUrl || this.data?.apiUrl;
    console.log('[Notes Component] URLs resolved:', { notesUrl, latestDataUrl });

    const notes$ = this.http.get<{ notes: NoteItem[] }>(notesUrl).pipe(
      map(res => {
        console.log('[Notes Component] Notes config loaded:', res?.notes?.length, 'items');
        return res?.notes ?? [];
      }),
      catchError((err) => {
        console.error('[Notes Component] Failed to load notes config:', err);
        return of([]);
      })
    );

    const isLocal = !latestDataUrl || latestDataUrl.startsWith('assets/') || latestDataUrl.startsWith('/assets/');
    const latest$ = latestDataUrl
      ? (isLocal
          ? this.http.get<any>(latestDataUrl)
          : this.configService.getConfig(latestDataUrl, {
              ...this.data,
              skipDefaultPayload: true,
              customPayload: {
                filter: {
                  where: [],
                  join: {},
                  orderBy: {}
                }
              }
            }, null, 'post')
        ).pipe(
          map(res => {
            console.log('[Notes Component] Raw latest data response:', res);
            return res;
          }),
          catchError((err) => {
            console.error('[Notes Component] Failed to load latest data from API:', err);
            return of(null);
          })
        )
      : of(null);

    forkJoin([notes$, latest$]).subscribe({
      next: ([notes, latest]) => {
        let data = latest;
        // Handle standard DVM response list
        if (latest && (latest as any).responseList?.length > 0) {
          const firstItem = (latest as any).responseList[0];
          if (firstItem?.data?.length > 0) {
            data = firstItem.data[0];
          }
        }
        // Handle .data wrapper or array
        data = (data as any)?.data || (Array.isArray(data) ? data[0] : data);
        
        const year = data?.year || data?.Year || data?.YEAR || '';
        const month = data?.month || data?.Month || data?.MONTH || '';
        
        console.log('[Notes Component] Resolved year/month:', { year, month });

        if (year || month) {
          this.notes = notes.map(note => ({
            ...note,
            text: note.text
              .replace(/{{YEAR}}/g, String(year))
              .replace(/{{MONTH}}/g, String(month))
          }));
        } else {
          console.warn('[Notes Component] No year/month data found in response, showing notes as-is.');
          this.notes = notes;
        }
        console.log('[Notes Component] Final notes prepared:', this.notes);
      },
      error: (err) => {
        console.error('[Notes Component] forkJoin error:', err);
      }
    });
  }

  trackById(_: number, note: { id: number }): number {
    return note.id;
  }
}
